import React, { useRef } from 'react';
import './text-area.css';
import Loader from '../loader/Loader';
import { debounce } from '../../utils';

interface Props {
	onChange?: (value: string, event: UIEvent) => any;
	onInput?: (value: string, event: UIEvent) => any;
	onBlur?: (value: string, event: UIEvent) => any;
	onDebouncedInput?: (value: string, event: UIEvent) => any;
	value?: string;
	defaultValue?: string;
	disabled?: boolean;
	color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'grey';
	variant?: 'underlined' | 'filled';
	size?: 'small' | 'medium' | 'large';
	loading?: boolean;
	required?: boolean;
	iconEnd?: string;
	label?: string;
	helper?: string;
	name?: string;
	placeholder?: string;
	onIconClick?: (event: UIEvent) => any;
	autocomplete?: string;
	rootProps?: {
		[key: string]: any;
	};
	inputProps?: {
		[key: string]: any;
	};
	[key: string]: any;
}

const defaultVariant = 'underlined';

export default function TextArea(props: Props) {
	const rootElementRef = useRef<HTMLDivElement>(null);

	const handleStateChange = (event: UIEvent): void => {
		const variant = props.variant ?? defaultVariant;
		const selectorClass = `.omrs-input-${variant}`;
		const rootElement = rootElementRef.current;

		// @ts-ignore
		const newValue = event.target.value;

		// @ts-ignore
		if (newValue.trim() !== '') {
			// If the input field is not empty after blur, add the class
			rootElement.classList.add('not-empty');
		} else {
			rootElement.classList.remove('not-empty');
		}
	};

	// Destructure all known props and leave other props to be cascaded down to the root element
	const {
		onChange,
		value,
		defaultValue,
		disabled,
		color,
		variant = defaultVariant,
		size,
		loading,
		iconEnd,
		required,
		label = '',
		helper = '',
		name = '',
		placeholder = '',
		autocomplete = '',
		rootProps = {},
		inputProps = {},
		onIconClick,
		onInput,
		onDebouncedInput,
		...otherProps
	} = props;

	const isLoading = (loading ?? false) && !disabled;
	const isDisabled = disabled ?? false;
	const isRequired = required ?? false;

	const { classes: rootClasses = [], ...remainingRootProps } = rootProps || {};
	const { classes: inputClasses = [], ...remainingInputProps } =
		inputProps || {};

	const debouncedInputHandler = debounce((value: any, event: any) => {
		// @ts-ignore
		onDebouncedInput?.(value, event);
	}, 200);

	return (
		<div
			className='omrs-input-group'
			ref={rootElementRef}
			{...remainingRootProps}
		>
			<label
				className={[
					'omrs-input',
					'omrs-input-' + variant,
					...(color ? ['omrs-input-' + color] : []),
					...(value ? ['not-empty'] : []),
				].join(' ')}
			>
				<textarea
					required={isRequired}
					disabled={isDisabled}
					name={name}
					autoComplete={autocomplete}
					placeholder={placeholder}
					className={[...inputClasses].join(' ')}
					onInput={(event) => {
						if (onInput) {
							handleStateChange(event as any);
							onInput?.(
								(event.target as HTMLTextAreaElement).value,
								event as any
							);
						}

						if (onDebouncedInput) {
							handleStateChange(event as any);

							debouncedInputHandler(
								(event.target as HTMLTextAreaElement).value,
								event
							);
						}
					}}
					onChange={(event) => {
						handleStateChange(event as any);
						onChange?.(
							(event.target as HTMLTextAreaElement).value,
							event as any
						);
					}}
					onBlur={(event) => {
						handleStateChange(event as any);
						props.onBlur?.(
							(event.target as HTMLTextAreaElement).value,
							event as any
						);
					}}
					value={value}
					defaultValue={defaultValue}
					style={{ maxWidth: '100%' }}
					{...remainingInputProps}
				>
					{value}
				</textarea>
				<span className='omrs-input-label'>{label}</span>
				<span
					className={[
						'omrs-input-helper',
						...(!helper ? ['invisible'] : []),
					].join(' ')}
				>
					{helper || 'helper'}
				</span>
				{(iconEnd || isLoading) && (
					<i
						className={[
							'omrs-input-icon',
							'fa-solid',
							'fa-' + (isLoading ? '' : iconEnd),
						].join(' ')}
						onClick={(event) => {
							if (onIconClick && !isLoading) {
								onIconClick(event as any);
							}
						}}
					>
						{isLoading && <Loader size={18} variant='text' />}
					</i>
				)}
			</label>
		</div>
	);
}
